import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  AccreditationStatusEnumType,
  DealStatusEnumType,
  FundingSourceEnumType,
  InvestmentEntityStatusEnumType,
} from '__generated__/graphql';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';

import Alert from 'primitives/Alert';
import LoadingIndicator from 'primitives/LoadingIndicator';

import InvestedBaskets from './InvestedBaskets';
import { LockedBasketsView } from './LockedBasketsView';

const INVESTMENT_BASKETS_QUERY = gql(`
  query InvestmentBaskets {
    dashboard {
      id
      investmentBasketRecommendations {
        id
        status
        dealTerms {
          fundingRoundName
          valuationOrCap
          financialInstrument
        }
        companyInformation {
          company {
            id
            name
            legalName
            image
          }
          industries
        }
        publicData {
          dealInviteLink
          description
        }
      }
      investmentEntities {
        id
        name
        status
        investorApplication {
          id
          accreditationStatus
        }
      }
    }
  }
`);

const CREATE_INVESTMENT_BASKET_MUTATION = gql(`
  mutation CreateInvestmentBasket(
    $investmentEntityId: ID!
    $investments: [InvestmentBasketInvestmentInputType!]!
    $name: String
    $description: String
  ) {
    createInvestmentBasket(
      investmentEntityId: $investmentEntityId
      investments: $investments
      name: $name
      description: $description
    ) {
      id
      name
      createdAt
      investmentEntity {
        id
        name
      }
      closings {
        id
        amount
        deal {
          id
          companyInformation {
            company {
              id
              name
            }
            industries
          }
        }
      }
    }
  }
`);

type Step = 'select' | 'confirm' | 'success';

export const MIN_INVESTMENT = 300000;
export const MAX_INVESTMENT = 10000000;
export const INVESTMENT_INCREMENT = 10000;
const TRANSACTION_FEE_PERCENTAGE = 0.02;

const BasketsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [investmentAmount, setInvestmentAmount] = useState(300000);
  const [selectedDeals, setSelectedDeals] = useState<string[]>([]);
  const [selectedInvestmentEntityId, setSelectedInvestmentEntityId] = useState<string>('');

  const { data, loading, error, refetch } = useQuery(INVESTMENT_BASKETS_QUERY);
  const [
    createInvestmentBasket,
    { loading: creatingBasket, error: mutationError, data: createdBasketData },
  ] = useMutation(CREATE_INVESTMENT_BASKET_MUTATION, {
    refetchQueries: ['InvestmentBasketsByEntityId', 'InvestmentBaskets'],
  });

  const recommendedDeals = useMemo(
    () => data?.dashboard.investmentBasketRecommendations ?? [],
    [data]
  );
  const investmentEntities = useMemo(
    () =>
      data?.dashboard.investmentEntities.filter(
        entity => entity.status === InvestmentEntityStatusEnumType.Active
      ) ?? [],
    [data]
  );

  const isAccredited = useMemo(() => {
    return investmentEntities.some(
      entity =>
        entity.investorApplication?.accreditationStatus === AccreditationStatusEnumType.Accredited
    );
  }, [investmentEntities]);

  useEffect(() => {
    if (recommendedDeals.length && selectedDeals.length === 0) {
      // Only select deals that are not FUNDED
      const selectableDeals = recommendedDeals
        .filter(
          deal =>
            deal.status === DealStatusEnumType.Raising || deal.status === DealStatusEnumType.Pooling
        )
        .map(deal => deal.id);
      setSelectedDeals(selectableDeals);
    }
  }, [recommendedDeals, selectedDeals.length]);

  useEffect(() => {
    if (investmentEntities.length && !selectedInvestmentEntityId) {
      setSelectedInvestmentEntityId(investmentEntities[0].id);
    }
  }, [investmentEntities, selectedInvestmentEntityId]);

  const selectedDealDetails = useMemo(
    () =>
      recommendedDeals.filter(
        deal =>
          selectedDeals.includes(deal.id) &&
          (deal.status === DealStatusEnumType.Raising || deal.status === DealStatusEnumType.Pooling)
      ),
    [recommendedDeals, selectedDeals]
  );

  const perDealAllocations = useMemo(() => {
    if (!selectedDealDetails.length) return [];
    const amounts: number[] = [];
    let remaining = investmentAmount;
    let dealsRemaining = selectedDealDetails.length;
    selectedDealDetails.forEach((_deal, index) => {
      if (index === selectedDealDetails.length - 1) {
        amounts.push(Math.max(remaining, 0));
      } else {
        const idealAmount = Math.round(remaining / dealsRemaining);
        amounts.push(idealAmount);
        remaining -= idealAmount;
        dealsRemaining -= 1;
      }
    });
    return amounts;
  }, [investmentAmount, selectedDealDetails]);

  const transactionFee = Math.round(investmentAmount * TRANSACTION_FEE_PERCENTAGE);
  const totalCommitment = investmentAmount + transactionFee;

  const basketSummary = createdBasketData?.createInvestmentBasket;

  const handleInvestmentAmountChange = (value: number) => {
    if (Number.isNaN(value)) return;
    const sanitized = Math.min(value, MAX_INVESTMENT);
    setInvestmentAmount(sanitized);
  };

  const handleToggleDeal = (dealId: string) => {
    // Find the deal to check if it's FUNDED
    const deal = recommendedDeals.find(d => d.id === dealId);
    // Prevent selecting FUNDED deals
    if (
      deal &&
      deal.status !== DealStatusEnumType.Raising &&
      deal.status !== DealStatusEnumType.Pooling
    ) {
      return;
    }
    setSelectedDeals(current =>
      current.includes(dealId) ? current.filter(id => id !== dealId) : [...current, dealId]
    );
  };

  const handleProceedToConfirmation = () => navigate('/invest/baskets/confirm');

  const handleSubmitBasket = async () => {
    if (!selectedInvestmentEntityId || !selectedDealDetails.length) return;
    const investments = selectedDealDetails.map((deal, index) => ({
      dealId: deal.id,
      amount: perDealAllocations[index],
      fundingSource: FundingSourceEnumType.IndianAccount,
    }));

    const response = await createInvestmentBasket({
      variables: {
        investmentEntityId: selectedInvestmentEntityId,
        investments,
        name: `Investment Case- ${dayjs().format('DD MMM YYYY')}`,
        description: `Auto-created case on ${dayjs().format('DD MMM YYYY')}`,
      },
    });

    if (response.data?.createInvestmentBasket.id) {
      navigate('/invest/baskets/success');
    }
  };

  const currentStep: Step = location.pathname.endsWith('/confirm')
    ? 'confirm'
    : location.pathname.endsWith('/success')
    ? 'success'
    : 'select';

  const outletContext = {
    recommendedDeals,
    selectedDeals,
    handleToggleDeal,
    selectedDealDetails,
    perDealAllocations,
    investmentAmount,
    handleInvestmentAmountChange,
    investmentEntities,
    selectedInvestmentEntityId,
    setSelectedInvestmentEntityId,
    transactionFee,
    totalCommitment,
    mutationError,
    creatingBasket,
    handleSubmitBasket,
    handleProceedToConfirmation,
    basketSummary,
  };

  const renderStepSubtitle = () => {
    switch (currentStep) {
      case 'confirm':
        return 'Confirm your investment split';
      case 'success':
        return 'Your commitment is confirmed';
      default:
        return 'Invest in multiple world-class companies with just one-click';
    }
  };

  const renderContent = () => {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    return (
      <section className="space-y-10">
        {investmentEntities.length > 0 && (
          <div className="space-y-8">
            {investmentEntities.map(entity => (
              <InvestedBaskets
                key={entity.id}
                investmentEntityId={entity.id}
                investmentEntityName={entity.name}
              />
            ))}
          </div>
        )}
        {currentStep === 'select' && (
          <div>
            {!recommendedDeals.length ? (
              <>
                <div className="mb-6 space-y-2">
                  <p className="text-sm text-slate-500 uppercase tracking-wide">Create New Case</p>
                  <h2 className="text-2xl font-semibold text-slate-900">
                    Featured Investment Opportunities
                  </h2>
                </div>
                <Alert
                  title="No deals available"
                  message="We will recommend new investment opportunities as soon as fresh deals open up."
                  type="info"
                />
              </>
            ) : !isAccredited ? (
              <LockedBasketsView recommendedDeals={recommendedDeals} />
            ) : (
              <div className="mb-12">
                <Outlet context={outletContext} />
              </div>
            )}
          </div>
        )}
        {currentStep !== 'select' && (
          <div className="mb-12">
            <Outlet context={outletContext} />
          </div>
        )}
      </section>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Encase"
        subTitle={renderStepSubtitle()}
        onBackButtonClick={currentStep === 'select' ? undefined : () => navigate('/invest/baskets')}
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default BasketsPage;

export { default as BasketsSelectStep } from './SelectStep';
export { default as BasketsConfirmStep } from './ConfirmStep';
export { default as BasketsSuccessStep } from './SuccessStep';
